/**
 * modulesConfig — derives the two-tier navigation model (module rail +
 * contextual sub-nav) from the existing `sidebarSections` tree.
 *
 * Each top-level section becomes a "module" (rail icon). A module exposes
 * `groups` (optional labelled groups of leaf links) for the sub-nav panel,
 * plus flattened helpers used by the command palette, breadcrumbs and
 * active-module resolution.
 */
import {
  Boxes,
  Building2,
  Store,
  Package,
  Users,
  ShoppingCart,
  UserCog,
  FolderKanban,
  FileSearch,
  TrendingUp,
  Wallet,
  CreditCard,
  Layers,
  Warehouse,
  RotateCcw,
  Shield,
  Tag,
  Landmark,
  ShieldCheck,
  Wrench,
} from 'lucide-react';

import { sidebarSections } from './menuConfig';

export const slugify = (s = '') =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export const prettify = (s = '') =>
  s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const MODULE_ICONS = {
  'LOW-CODE': Boxes,
  'ENTERPRISE STRUCTURE': Building2,
  'VENDOR MASTER': Store,
  'MATERIAL MASTER': Package,
  'CUSTOMER MASTER': Users,
  'PROCURE-TO-PAY': ShoppingCart,
  'HUMAN RESOURCES': UserCog,
  'PROJECT SYSTEMS': FolderKanban,
  'PRE-SALES': FileSearch,
  'ORDER-TO-CASH': TrendingUp,
  'ACCOUNTS RECEIVABLE': Wallet,
  'ACCOUNTS PAYABLE': CreditCard,
  'INVENTORY MGMT': Layers,
  WAREHOUSE: Warehouse,
  'VENDOR RETURNS': RotateCcw,
  'RELEASE STRATEGY': Shield,
  PRICING: Tag,
  'ACCESS CONTROL': Shield,
  'ASSET ACCOUNTING': Landmark,
  'QUALITY MANAGEMENT': ShieldCheck,
  'PLANT MAINTENANCE': Wrench,
};

// Maps a top-level module (by sidebar section title) to its primary DocType.
// Used for per-role sidebar gating: a user who cannot READ the doctype won't
// see the module. Modules not listed here are always visible (subject to
// adminOnly). Keep keys in sync with menuConfig section titles.
export const MODULE_DOCTYPE = {
  'VENDOR MASTER': 'vendor',
  'MATERIAL MASTER': 'material',
  'CUSTOMER MASTER': 'customer',
  'PROCURE-TO-PAY': 'purchase_order',
  'ORDER-TO-CASH': 'sales_order',
  'HUMAN RESOURCES': 'employee',
  'ASSET ACCOUNTING': 'asset_master',
  'QUALITY MANAGEMENT': 'inspection_lot',
  'PLANT MAINTENANCE': 'maintenance_order',
};

const buildGroups = (section) => {
  if (section.isParent) {
    return section.items.map((sub) => ({
      label: sub.title,
      badge: sub.badge,
      leaves: (sub.items || []).filter((i) => i.path),
    }));
  }
  const top = [];
  const groups = [];
  (section.items || []).forEach((it) => {
    if (it.isNested && it.children) {
      groups.push({ label: it.label, leaves: it.children.filter((c) => c.path) });
    } else if (it.path) {
      top.push(it);
    }
  });
  const out = [];
  if (top.length) out.push({ label: null, leaves: top });
  return out.concat(groups);
};

export const modules = sidebarSections.map((section) => {
  const groups = buildGroups(section);
  const _leaves = groups.flatMap((g) => g.leaves);
  return {
    id: slugify(section.title),
    title: section.title,
    name: prettify(section.title),
    badge: section.badge,
    badgeColor: section.badgeColor,
    icon: MODULE_ICONS[section.title] || Layers,
    adminOnly: !!section.adminOnly,
    auditOnly: !!section.auditOnly,
    doctype: MODULE_DOCTYPE[section.title] || null,
    groups,
    _leaves,
  };
});

export const flatPages = modules.flatMap((m) =>
  m.groups.flatMap((g) =>
    g.leaves.map((l) => ({
      path: l.path,
      base: l.path.split('?')[0],
      label: l.label,
      icon: l.icon,
      moduleId: m.id,
      moduleTitle: m.title,
      group: g.label,
    })),
  ),
);

/** Longest-base-prefix match → which module owns this pathname. */
export const resolveModuleId = (pathname) => {
  if (!pathname || pathname === '/') return null;
  let best = null;
  let bestLen = -1;
  for (const p of flatPages) {
    if (pathname === p.base || pathname.startsWith(`${p.base}/`)) {
      if (p.base.length > bestLen) {
        bestLen = p.base.length;
        best = p.moduleId;
      }
    }
  }
  return best;
};

/** Resolve the active page (exact base, else closest parent) for breadcrumbs. */
export const findPage = (pathname) => {
  if (!pathname || pathname === '/') return null;
  const exact = flatPages.find((p) => p.base === pathname);
  if (exact) return exact;
  let best = null;
  let bestLen = -1;
  for (const p of flatPages) {
    if (pathname === p.base || pathname.startsWith(`${p.base}/`)) {
      if (p.base.length > bestLen) {
        bestLen = p.base.length;
        best = p;
      }
    }
  }
  return best;
};
